// resolver.js
function createResolver(items, opts) {
   opts = opts || {};
   const debug = !!opts.debug;

   const byId = new Map();
   for (const it of items || []) byId.set(it.id, it);

   function getItem(id) {
      return byId.get(id) || null;
   }

   function traceObj() {
      return debug ? { steps: [] } : null;
   }

   function addStep(trace, msg) {
      if (trace) trace.steps.push(msg);
   }

   function* walkParents(startId, trace) {
      const visited = new Set();
      let cur = getItem(startId);

      while (cur) {
         if (visited.has(cur.id)) {
            addStep(trace, `Cycle detected at ${cur.id}`);
            break;
         }
         visited.add(cur.id);

         yield cur;

         const parentId = cur.idParent == null ? null : cur.idParent;
         if (parentId == null) break;
         cur = getItem(parentId);
      }
   }

   function findNearestRepeaterGroup(startId, trace) {
      for (const node of walkParents(startId, trace)) {
         if (node.idItemType === 16) {
            addStep(trace, `Nearest repeater group: ${node.id}`);
            return node;
         }
      }
      addStep(trace, 'No repeater group found');
      return null;
   }

   function findParentRepeater(groupItem, trace) {
      if (!groupItem) return null;
      const parentId = groupItem.idParent == null ? null : groupItem.idParent;
      const parent = parentId ? getItem(parentId) : null;

      if (parent && parent.idItemType === 15) {
         addStep(trace, `Parent repeater: ${parent.id}`);
         return parent;
      }

      addStep(trace, 'Group parent is not a repeater (15)');
      return null;
   }

   function coerceId(v) {
      if (v === null || v === undefined) return null;
      return String(v);
   }

   function findRowBySelectedId(repeaterItem, selectedRowId, trace) {
      const rows = repeaterItem?.options?.listOptions;
      if (!Array.isArray(rows)) {
         addStep(trace, 'Repeater has no listOptions[]');
         return null;
      }

      const wanted = coerceId(selectedRowId);
      if (wanted == null) {
         addStep(trace, 'No selected row id');
         return null;
      }

      const idField = repeaterItem?.options?.binding?.idField || 'id';

      for (const row of rows) {
         const rowId = coerceId(row && row[idField]);
         if (rowId === wanted) {
            addStep(trace, `Matched row via ${idField}=${rowId}`);
            return row;
         }
      }

      addStep(trace, `No row found for selected id=${wanted}`);
      return null;
   }

   function lookupNearestRepeaterRowColumn(itemId, columnName) {
      const trace = traceObj();

      const item = getItem(itemId);
      if (!item) return { value: null, trace };

      const group = findNearestRepeaterGroup(itemId, trace);
      if (!group) return { value: null, trace };

      const selectedRowId = group?.response?.val;
      addStep(trace, `Selected row id: ${selectedRowId}`);

      const repeater = findParentRepeater(group, trace);
      if (!repeater) return { value: null, trace };

      const row = findRowBySelectedId(repeater, selectedRowId, trace);
      if (!row) return { value: null, trace };

      if (!Object.prototype.hasOwnProperty.call(row, columnName)) {
         addStep(trace, `Row missing column '${columnName}'`);
         return { value: null, trace };
      }

      const val = row[columnName];
      addStep(trace, `Resolved ${columnName}=${val}`);
      return { value: val == null ? null : String(val), trace };
   }

   function resolveCalculated(itemId) {
      const trace = traceObj();

      const item = getItem(itemId);
      if (!item) return { value: null, trace };

      const calc = item?.options?.calculated;
      if (!calc || !calc.type) {
         addStep(trace, 'No options.calculated');
         return { value: null, trace };
      }

      if (calc.type === 'lookup') {
         const col = calc.valueColumn;
         if (!col) {
            addStep(trace, 'Calculated lookup missing valueColumn');
            return { value: null, trace };
         }

         const res = lookupNearestRepeaterRowColumn(itemId, col);

         // Merge lookup trace (debug only) so you see the walk path
         if (debug && res.trace && trace) trace.steps.push(...res.trace.steps);

         // Optional: noBindingType handling hook (fail-soft either way)
         // Right now we just return null if unbound, consistent with your lookup function.
         if (res.value == null) {
            addStep(trace, `Calculated lookup unresolved (noBindingType=${calc.noBindingType})`);
            return { value: null, trace };
         }

         addStep(trace, `Calculated lookup resolved ${col}=${res.value}`);
         return { value: res.value, trace };
      }

      addStep(trace, `Unsupported calculated.type: ${calc.type}`);
      return { value: null, trace };
   }

   function resolveTextOrLookup(itemId, desc) {
      const trace = traceObj();
      if (!desc || !desc.type) return { value: null, trace };

      if (desc.type === 'text') {
         return { value: desc.value == null ? null : String(desc.value), trace };
      }

      if (desc.type === 'lookup') {
         const col = desc.value;
         if (!col) return { value: null, trace };

         const res = lookupNearestRepeaterRowColumn(itemId, col);
         // merge trace if debugging
         if (debug && res.trace && trace) trace.steps.push(...res.trace.steps);
         return { value: res.value, trace };
      }

      return { value: null, trace };
   }

   function getPlaceholder(itemId) {
      const item = getItem(itemId);
      return resolveTextOrLookup(itemId, item?.options?.placeholder);
   }

   function getPrefix(itemId) {
      const item = getItem(itemId);
      return resolveTextOrLookup(itemId, item?.options?.prefix);
   }

   function getSuffix(itemId) {
      const item = getItem(itemId);
      return resolveTextOrLookup(itemId, item?.options?.suffix);
   }

   function getDisplayValue(itemId) {
      const trace = traceObj();
      const item = getItem(itemId);
      if (!item) return { value: null, trace };

      if (item.idItemType === 17) {
         addStep(trace, 'Display from calculated item (type 17)');
         const res = resolveCalculated(itemId);
         if (debug && res.trace && trace) trace.steps.push(...res.trace.steps);
         return { value: res.value, trace };
      }

      const v = item?.response?.val;
      addStep(trace, `Display from response.val: ${v}`);
      return { value: v == null ? null : v, trace };
   }

   return {
      getPlaceholder,
      getPrefix,
      getSuffix,
      getDisplayValue,
      lookupNearestRepeaterRowColumn,
      resolveCalculated
   };
}

module.exports = { createResolver };
